// src/contexts/NotificationContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import notificationService from '../services/notification.service';
import { io } from 'socket.io-client';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [socketConnected, setSocketConnected] = useState(false);
    const socketRef = useRef(null);

    // Socket.io connection setup
    useEffect(() => {
        if (!user?.id) return;

        const authToken = localStorage.getItem('authToken');
        if (!authToken) return;

        // Get backend URL from environment - use SAME URL as API (no port change)
        const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

        console.log('🔌 Attempting Socket.io connection to backend...', BACKEND_URL);

        // Create Socket.io connection with better error handling
        const socket = io(BACKEND_URL, {
            auth: {
                token: authToken
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 3, // Reduced attempts to avoid console spam
            timeout: 5000, // Add timeout
            forceNew: true // Force new connection each time
        });

        // Connection established
        socket.on('connect', () => {
            console.log('✅ Socket.io connected');
            setSocketConnected(true);
        });

        // Listen for real-time notifications
        socket.on('notification', (notification) => {
            console.log('🔔 Received real-time notification:', notification);

            // Add notification to state
            setNotifications(prev => {
                // Check if notification already exists by ID
                const existsById = prev.find(n => n.id === notification.id);
                if (existsById) return prev;

                // Check if similar notification exists (content-based deduplication)
                const existsByContent = prev.find(n =>
                    n.userId === notification.userId &&
                    n.type === notification.type &&
                    n.title === notification.title &&
                    n.message === notification.message &&
                    Math.abs(new Date(n.timestamp) - new Date(notification.timestamp)) < 5 * 60 * 1000
                );
                if (existsByContent) return prev;

                return [notification, ...prev];
            });
        });

        // Connection error
        socket.on('connect_error', (error) => {
            console.log('⚠️ Socket.io not available - real-time notifications disabled');
            setSocketConnected(false);
            // Don't log as error to reduce console noise
        });

        // Disconnected
        socket.on('disconnect', (reason) => {
            console.log('🔌 Socket.io disconnected:', reason);
            setSocketConnected(false);
        });

        // Reconnecting
        socket.on('reconnect_attempt', (attemptNumber) => {
            console.log(`🔄 Socket.io reconnecting... Attempt ${attemptNumber}`);
        });

        // Reconnected
        socket.on('reconnect', (attemptNumber) => {
            console.log(`✅ Socket.io reconnected after ${attemptNumber} attempts`);
            setSocketConnected(true);
            // Refresh notifications after reconnection
            fetchNotifications();
        });

        socketRef.current = socket;

        // Cleanup on unmount
        return () => {
            console.log('🔌 Disconnecting Socket.io...');
            socket.disconnect();
        };
    }, [user?.id]);

    // Helper function to deduplicate notifications
    const deduplicateNotifications = (notifications) => {
        if (!Array.isArray(notifications)) return [];

        const uniqueNotifications = [];
        const seenKeys = new Set();

        // Sort by timestamp (newest first)
        const sorted = [...notifications].sort((a, b) => {
            const timeA = new Date(a.timestamp || 0).getTime();
            const timeB = new Date(b.timestamp || 0).getTime();
            return timeB - timeA;
        });

        for (const notification of sorted) {
            // Create a unique key based on content
            // Include userId, type, title, and message to identify duplicates
            const contentKey = JSON.stringify({
                userId: notification.userId,
                type: notification.type,
                title: notification.title,
                message: notification.message
            });

            // Also create a time-based key to handle near-duplicates (within 10 minutes)
            const timestamp = new Date(notification.timestamp || 0).getTime();
            const timeSlot = Math.floor(timestamp / (10 * 60 * 1000)); // 10-minute slots
            const timedKey = `${contentKey}_${timeSlot}`;

            // Check if we've seen this exact notification recently
            if (!seenKeys.has(timedKey)) {
                seenKeys.add(timedKey);
                uniqueNotifications.push(notification);
            }
            // Otherwise, skip this duplicate
        }

        console.log(`🔍 Deduplication: ${notifications.length} → ${uniqueNotifications.length} notifications`);
        return uniqueNotifications;
    };

    // Fetch notifications from backend
    const fetchNotifications = useCallback(async () => {
        if (!user?.id) return;

        const authToken = localStorage.getItem('authToken');

        setLoading(true);
        try {
            // Try backend first
            if (authToken) {
                const data = await notificationService.getNotifications();
                const deduplicated = deduplicateNotifications(data);
                setNotifications(deduplicated);
                // Save to localStorage for offline access
                notificationService.saveToLocalStorage(deduplicated);
            } else {
                // Fallback to localStorage
                const stored = notificationService.getFromLocalStorage();
                const deduplicated = deduplicateNotifications(stored);
                setNotifications(deduplicated);
            }
        } catch (error) {
            console.error('Error fetching notifications, using localStorage:', error);
            // Fallback to localStorage on error
            const stored = notificationService.getFromLocalStorage();
            const deduplicated = deduplicateNotifications(stored);
            setNotifications(deduplicated);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    // Load notifications on mount and when user changes
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        const authToken = localStorage.getItem('authToken');

        try {
            // Try backend first
            if (authToken) {
                await notificationService.markAsRead(notificationId);
            }

            // Update local state
            setNotifications(prev =>
                prev.map(notif =>
                    notif.id === notificationId
                        ? { ...notif, read: true }
                        : notif
                )
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
            // Still update local state even if backend fails
            setNotifications(prev =>
                prev.map(notif =>
                    notif.id === notificationId
                        ? { ...notif, read: true }
                        : notif
                )
            );
        }
    };

    // Mark all notifications as read
    const markAllAsRead = async () => {
        const authToken = localStorage.getItem('authToken');

        try {
            // Try backend first
            if (authToken) {
                await notificationService.markAllAsRead();
            }

            // Update local state
            setNotifications(prev =>
                prev.map(notif => ({ ...notif, read: true }))
            );
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            // Still update local state even if backend fails
            setNotifications(prev =>
                prev.map(notif => ({ ...notif, read: true }))
            );
        }
    };

    // Delete notification
    const deleteNotification = async (notificationId) => {
        const authToken = localStorage.getItem('authToken');

        try {
            // Try backend first
            if (authToken) {
                await notificationService.deleteNotification(notificationId);
            }

            // Update local state
            setNotifications(prev =>
                prev.filter(notif => notif.id !== notificationId)
            );
        } catch (error) {
            console.error('Error deleting notification:', error);
            // Still update local state even if backend fails
            setNotifications(prev =>
                prev.filter(notif => notif.id !== notificationId)
            );
        }
    };

    // Clear all notifications
    const clearAll = async () => {
        const authToken = localStorage.getItem('authToken');

        try {
            // Try backend first
            if (authToken) {
                await notificationService.clearAll();
            }

            // Update local state
            setNotifications([]);
        } catch (error) {
            console.error('Error clearing notifications:', error);
            // Still update local state even if backend fails
            setNotifications([]);
        }
    };

    // Add new notification
    const addNotification = async (notification) => {
        const authToken = localStorage.getItem('authToken');

        const newNotification = {
            timestamp: new Date().toISOString(),
            read: false,
            ...notification
        };

        // //console.log('➕ Adding notification:', {
        //     userId: newNotification.userId,
        //     type: newNotification.type,
        //     title: newNotification.title,
        //     currentCount: notifications.length
        // });

        try {
            // Try backend first
            if (authToken) {
                const created = await notificationService.createNotification(newNotification);
                // Update local state with backend response
                setNotifications(prev => {
                    const updated = [created, ...prev];
                    // //console.log('📊 Notifications after add:', {
                    //     count: updated.length,
                    //     latest: updated[0],
                    //     allUserIds: [...new Set(updated.map(n => n.userId))]
                    // });
                    return updated;
                });
                return created.id;
            } else {
                // Fallback to localStorage
                const localNotif = {
                    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                    ...newNotification
                };
                setNotifications(prev => {
                    const updated = [localNotif, ...prev];
                    // //console.log('📊 Notifications after add (localStorage):', {
                    //     count: updated.length,
                    //     latest: updated[0]
                    // });
                    return updated;
                });
                return localNotif.id;
            }
        } catch (error) {
            console.error('Error creating notification:', error);
            // Fallback to localStorage on error
            const localNotif = {
                id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                ...newNotification
            };
            setNotifications(prev => [localNotif, ...prev]);
            return localNotif.id;
        }
    };

    // Get notifications for a specific user
    const getNotificationsForUser = (userId) => {
        if (!userId || !notifications || !Array.isArray(notifications)) {
            return [];
        }
        return notifications.filter(notif => notif && notif.userId === userId);
    };

    // Get unread count for a user
    const getUnreadCount = (userId) => {
        // Add null safety for both userId and notifications
        if (!userId || !notifications || !Array.isArray(notifications)) {
            return 0;
        }
        return notifications.filter(notif => notif && notif.userId === userId && !notif.read).length;
    };

    // Send onboarding completion notification
    const sendOnboardingNotification = async (employeeId, employeeName, designation, legalEntity, managerId = null) => {
        const authToken = localStorage.getItem('authToken');

        // //console.log('📧 sendOnboardingNotification called:', {
        //     employeeId,
        //     employeeName,
        //     designation,
        //     legalEntity,
        //     managerId
        // });

        try {
            // Try backend first
            if (authToken) {
                await notificationService.sendOnboardingNotification(
                    employeeId,
                    employeeName,
                    designation,
                    legalEntity,
                    managerId
                );
                // Refresh notifications to get the newly created ones
                await fetchNotifications();
                //console.log('✅ Onboarding notifications sent via backend');
                return;
            }
        } catch (error) {
            console.error('Error sending onboarding notification via backend, using fallback:', error);
        }

        // Fallback to local notification creation
        // Send notification to the employee
        const employeeNotificationId = await addNotification({
            userId: employeeId,
            type: 'onboarding_complete',
            title: 'Onboarding Completed',
            message: `You have successfully been onboarded as ${designation} to ${legalEntity}. Welcome to our Organization.`,
            icon: 'CheckCircle',
            color: 'green'
        });

        // //console.log(`📧 Notification created for employee ${employeeId}:`, {
        //     notificationId: employeeNotificationId,
        //     userId: employeeId,
        //     type: 'onboarding_complete',
        //     allNotifications: notifications.length
        // });

        // Send notification to the reporting manager if exists
        if (managerId) {
            const managerNotificationId = await addNotification({
                userId: managerId,
                type: 'team_onboarding',
                title: 'New Team Member Onboarded',
                message: `${employeeName} has been successfully onboarded as ${designation} to ${legalEntity}. Please welcome ${employeeName} to the Team.`,
                icon: 'Users',
                color: 'blue'
            });

            // //console.log(`📧 Notification created for manager ${managerId}:`, {
            //     notificationId: managerNotificationId,
            //     userId: managerId,
            //     type: 'team_onboarding',
            //     employeeName,
            //     allNotifications: notifications.length
            // });
        }
    };

    // Get unread count - add additional null safety
    const unreadCount = (user && user.id) ? getUnreadCount(user.id) : 0;

    const value = {
        notifications,
        loading,
        unreadCount,
        socketConnected,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAll,
        addNotification,
        getNotificationsForUser,
        getUnreadCount,
        sendOnboardingNotification
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
